import { formatCurrency, formatDate } from './formatters';
import { AREA_UNITS, WEIGHT_UNITS, EXPENSE_PHOTO, SELLING_PHOTO, USER_IMAGE } from './constants';
import logoTitlePng from '../icons/logo-title.png';

async function toBase64(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateCropPDF({ crop, expenses, sellings, user }) {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const totalExpense = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalSelling = sellings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const profitLoss   = totalSelling - totalExpense;
  const areaLabel    = crop?.area ? `${crop.area} ${AREA_UNITS[crop.area_unit] || crop.area_unit || ''}` : '-';

  // Pre-fetch all images as base64 so they're embedded and load instantly
  const [logoB64, userImgB64] = await Promise.all([
    toBase64(logoTitlePng),
    user?.photo_url ? toBase64(USER_IMAGE(user.photo_url)) : Promise.resolve(null),
  ]);

  const imgLink = (url) => url
    ? `<a href="${url}" target="_blank" style="color:#1a6b2a;font-size:12px;font-weight:600;text-decoration:underline">See Photo</a>`
    : '<span style="color:#ccc;font-size:11px">—</span>';

  const expenseRows = expenses.length
    ? expenses.map((e) => `
        <tr>
          <td>${formatDate(e.expense_date)}</td>
          <td>${e.kharcho || '-'}</td>
          <td>${e.details || '-'}</td>
          <td>${imgLink(e.photo ? EXPENSE_PHOTO(e.photo) : null)}</td>
          <td style="color:#D8583D;font-weight:600">${formatCurrency(e.amount)}</td>
        </tr>`).join('')
    : '<tr><td colspan="5" style="text-align:center;color:#aaa;padding:16px">No expenses</td></tr>';

  const sellingRows = sellings.length
    ? sellings.map((s) => `
        <tr>
          <td>${formatDate(s.sell_date)}</td>
          <td>${s.vepari || s.buyer_name || '-'}</td>
          <td>${s.weight ? `${s.weight} ${WEIGHT_UNITS[s.weightUnit] || s.weightUnit}` : '-'}</td>
          <td>${s.rate ? `${formatCurrency(s.rate)} / ${s.rateUnit}` : '-'}</td>
          <td>${imgLink(s.photo_url || (s.photo ? SELLING_PHOTO(s.photo) : null))}</td>
          <td style="color:#4ebf53;font-weight:600">${formatCurrency(s.amount)}</td>
        </tr>`).join('')
    : '<tr><td colspan="6" style="text-align:center;color:#aaa;padding:16px">No sales</td></tr>';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${crop?.crop_name || crop?.crop || 'Crop'} — Kheti Hisaab</title>
  <style>
    body { margin:0; padding:28px; font-family:Arial,sans-serif; font-size:13px; color:#222; }
    .container { width:90%; margin:0 auto; }
    .header { display:flex; justify-content:space-between; align-items:center; margin-bottom:40px; }
    table { width:100%; margin:12px 0 20px; border-collapse:collapse; border:1px solid #d9d9d9; }
    th { background:#1a6b2a; color:#fff; padding:10px; text-align:left; font-size:13px; }
    td { padding:8px 10px; vertical-align:middle; }
    tr:nth-child(even) td { background:#f5f5f5; }
    tfoot th { background:transparent; font-weight:700; }
    tfoot th.danger { color:#D8583D; }
    tfoot th.success { color:#4ebf53; }
    h3 { margin:24px 0 4px; color:#1a6b2a; }
    .print-bar { position:sticky; top:0; z-index:100; background:#1a6b2a; padding:10px 5%; display:flex; justify-content:flex-end; gap:10px; }
    .print-bar button { padding:8px 20px; border:none; border-radius:6px; font-size:13px; font-weight:600; cursor:pointer; }
    .btn-print { background:#fff; color:#1a6b2a; }
    .btn-close { background:rgba(255,255,255,0.15); color:#fff; }
    @media print { .print-bar { display:none; } @page { margin:15mm; } body { padding:0; } }
  </style>
</head>
<body>
  <div class="print-bar">
    <button class="btn-close" onclick="window.close()">Close</button>
    <button class="btn-print" onclick="window.print()">⬇ Download PDF</button>
  </div>
  <div class="container">
    <div class="header">
      <div style="display:flex;align-items:center;gap:14px">
        ${userImgB64 ? `<img src="${userImgB64}" alt="${user?.name || ''}" style="width:56px;height:56px;border-radius:50%;object-fit:cover;flex-shrink:0"/>` : ''}
        <div>
          ${(user?.farm_name || user?.farm) ? `<h2 style="margin:0 0 4px">${user.farm_name || user.farm}</h2>` : ''}
          <h2 style="margin:0 0 4px">${user?.name || ''}</h2>
          <div style="color:#888;font-size:12px">${today}</div>
        </div>
      </div>
      ${logoB64 ? `<img src="${logoB64}" alt="Kheti Hisaab" style="height:48px;object-fit:contain"/>` : ''}
    </div>

    <table border="1">
      <thead>
        <tr>
          <th>Sowing Date</th><th>Crop</th><th>Variety</th><th>Farm</th><th>Area</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${formatDate(crop?.crop_date)}</td>
          <td style="text-transform:capitalize">${crop?.crop_name || crop?.crop || '-'}</td>
          <td>${crop?.verity || '-'}</td>
          <td style="text-transform:capitalize">${crop?.farm_name || '-'}</td>
          <td>${areaLabel}</td>
        </tr>
      </tbody>
    </table>

    <h3>Expenses</h3>
    <table border="1">
      <thead>
        <tr><th>Date</th><th>Type</th><th>Note</th><th>Photo</th><th>Amount</th></tr>
      </thead>
      <tbody>${expenseRows}</tbody>
      <tfoot>
        <tr>
          <th class="danger" colspan="4">Total Expense</th>
          <th class="danger">${formatCurrency(totalExpense)}</th>
        </tr>
      </tfoot>
    </table>

    <h3>Sales / Income</h3>
    <table border="1">
      <thead>
        <tr><th>Date</th><th>Buyer</th><th>Quantity</th><th>Rate</th><th>Photo</th><th>Amount</th></tr>
      </thead>
      <tbody>${sellingRows}</tbody>
      <tfoot>
        <tr>
          <th class="success" colspan="5">Total Sales</th>
          <th class="success">${formatCurrency(totalSelling)}</th>
        </tr>
      </tfoot>
    </table>

    <div style="text-align:right;font-size:15px;font-weight:700;margin-top:12px;color:${profitLoss >= 0 ? '#4ebf53' : '#D8583D'}">
      ${profitLoss >= 0
        ? `Profit: ${formatCurrency(profitLoss)}`
        : `Loss: ${formatCurrency(Math.abs(profitLoss))}`}
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}
